import { Alert } from '@/shared/components/rebranded/Alert/Alert';
import Radio from '@/shared/components/rebranded/Radio/Radio';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import { ChangeEvent } from 'react';
import { FieldSet } from '../../shared/components/Form/Form';
import {
  ErrorMessage,
  FieldProps,
  MessageWithArgs,
  StringSchema
} from '../types';
import { zip } from '../validation/utils';
import { hasErrors } from '../validation/validateSchema';

export type Props = FieldProps<StringSchema>;

export const RadioGroupField = ({
  schema,
  id,
  value,
  onChange,
  touched,
  errors,
  forceShowError,
  readOnly,
  name
}: Props) => {
  const { getText } = useTranslations();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    return onChange({
      target: {
        name,
        value: e.target.value
      }
    });
  };

  const transformedErrors = transformErrors(errors);
  const showError = !!(
    (touched || forceShowError) &&
    hasErrors(transformedErrors)
  );

  const options = zip(schema.enum!, schema.enumNames).map(
    ([optionId, optionLabel]) => {
      return {
        label: optionLabel!,
        id: `${name}_${optionId!}`,
        value: optionId!
      };
    }
  );

  return (
    <FieldSet>
      <Radio
        id={id}
        name={name}
        options={options}
        label={schema.title}
        hint={schema.$uiOptions?.hint}
        disabled={readOnly}
        onChange={handleChange}
        value={String(value)}
      />
      {showError &&
        transformedErrors &&
        transformedErrors.map(error => (
          <div key={error} style={{ marginTop: 8 }}>
            <Alert variant="error" fluid title={getText(error)} />
          </div>
        ))}
    </FieldSet>
  );
};

const transformErrors = (
  errors: Array<ErrorMessage> | undefined
): Array<string> | undefined => {
  if (!errors) {
    return undefined;
  }

  const stringErrors = errors
    .filter(error => typeof error === 'string')
    .map(error =>
      error === 'errors.required'
        ? 'errors.radiogroup.required'
        : (error as string)
    );

  const errorsWithArgs = errors
    .filter((error): error is MessageWithArgs => typeof error === 'object')
    .map(error => {
      return error.message;
    });

  return [...stringErrors, ...errorsWithArgs];
};
